import { CHECKED_STATUS } from '../../services/search/search.service';
import { HOOPLA_TYPE } from '../../services/thirdparty/hoopla/hoopla.service';

export interface IAuthoritySearchRequestPayload {
  query: string;
  searchId: string;
}

export interface AuthorityValue {
  value: string; //"Ali, Muhammad 1956-2015"
  searchIndex: string; //authority_100
}

export interface AuthorityResults {
  results: AuthorityMatches[];
}

export interface AuthorityMatches {
  values: AuthorityValue[];
  authorityType: string;
}

export interface SearchStatus {
  searchId: string;
}

export interface SearchRequestPayload {
  searchQuery?: SearchQuery;
  facetQuery?: SearchQuery;
}

export interface PollResultsRequestPayload {
  searchId: string;
  startRecord?: number;
  numOfRecords?: number;
  sortBy?: string[];
  orderBy?: string;
  resultType?: string;
  resultStatusOnly?: boolean;
}

export interface SearchResults {
  searchQuery?: SearchQuery;
  resultStatusOnly: boolean;
  resultsCount: number;
  hitsCount: number;
  totalGroups: number;
  totalTargets: number;
  activeTargets: number;
  targets: TargetSummary[];
  resultType: string;
  searchId: string;
  results: Result[];
  facets: Facet[];
  matches: Match[];
  enableScore?: boolean;
  maxScore?: number;
  solrQTime?: number;
  solrTotalRequestedTime?: number;
  pollTime?: number;
  jacketArtTime?: number;
  maxZeroResultsWaitTimeReached?: boolean;
  maxPollResultsWaitTimeReached?: boolean;
  maxWaitTimeReached?: boolean;
  sqlName?: string;
  apiUrl?: string;
  limitFacets?: Facet[];
  dateItemsAdded?: DropdownField[];
  trySearch?: TryObject;
  versoHoldingRequest?: VersoHoldingRequest;
  solrSpellSuggesterResponse?: SolrSpellSuggesterResponse;
}

export interface SearchQuery extends BaseQuery {
  matches?: Match[];
  filters?: Filter;
  facetMatches?: FacetMatch[];
  resources?: number[];
  searchType?: number;
  limitToQueryOnly?: boolean;
}

export interface BaseQuery {
  searchId?: string;
  startRecord?: number;
  numOfRecords?: number;
  facetOffset?: number;
  numOfFacetPerGroup?: number;
  sortBy?: string[];
  orderBy?: string;
  resultType?: string;
  includedVersoHoldings?: boolean;
  wait?: boolean;
  resultStatusOnly?: boolean;
  isKidsSearch?: boolean;
}
export interface Match {
  index: string;
  query: string;
  queryOption?: string;
  matchJoinOperator?: string;
}

export interface Filter {
  scopingLevel?: number;
  languages?: string[];
  pubYears?: string[];
  formats?: string[];
  materialTypes?: string[];
  forAudiences?: string[];
  lexile?: string[];
  awards?: string[];
  readingPrograms?: string[];
  dateItemsAdded?: string[];
  interestLevelAcceleratedReader?: string[];
  readingLevelAcceleratedReader?: string[];
  titlePointValueAcceleratedReader?: string[];
  readingLevelReadingCounts?: string[];
  titlePointValueReadingCounts?: string[];
  hasOclcNumber?: string[];
  clcdAge?: string[];
  clcdGrades?: string[];
}

export interface FacetMatch {
  index: string;
  query: string;
  facetId?: number;
  name?: string;
  show?: boolean;
  type?: string;
}

export interface DropdownField {
  index: string;
  name: string;
  value: string;
  count: string;
  selected: boolean;
}

export interface SearchField {
  index: string;
  value: string;
}

export interface TryObject {
  doTrySearch: boolean;
  tryMessage: string;
  tryList: TryItem[];
}

export interface TryItem {
  heading: string;
  searchId: string;
}

export interface Result {
  sequence: number;
  totalDocs: number;
  formats: DocFormat[];
  maxScore: number;
}

export interface DocFormat {
  isOnlyAG?: boolean; // ui property
  allAG?: boolean; // ui property
  allAV?: boolean; // ui property
  showRequest?: boolean; // ui property
  showList?: boolean; // ui property
  holdings?: any; // ui property
  show?: boolean; // ui property
  sequence: number;
  format: string;
  formatLong: string;
  numDocs: number;
  totalCopies: number; //For Shareit, this is total owning libraries.
  totalAvailables: number;
  totalOwningLibraries: number;
  totalHolds: number;
  documents: Document[];
  isMyLibrary: boolean;
}

export interface JacketArt {
  source: string;
  type: string;
  small: string;
  medium: string;
  large: string;
  link: string;
}

export interface Document {
  isActive: boolean; // hoopla
  purchaseModel: HOOPLA_TYPE;
  agControlId: any;
  title: string;
  author: string;
  authors: string[];
  isbn: string[];
  issn: string[];
  publisher: string;
  pubYear: string[];
  subjects: string[];
  upc: string[];
  oclc: string[];
  notes: string[];
  shard: string;
  libraryDbPoolKey: number;
  databaseProfileKey: number;
  targetName: string;
  format: string;
  format_Long: string;
  jacketArt: JacketArt;
  targetType: string;
  copies: number; //For Shareit, this is total owning libraries.
  availables: number;
  owningLibraries: number;
  availability: string;
  holds: number;
  dateAdded: string;
  readerFormats: string[];
  itemCallNumber: string[];
  hldLocation: string[];
  contentDetailsUrl: string;
  magazineId: string;
  issueId: string;
  country: string;
  audience: string;
  dateSource: string;
  holdingId: string;
  isRefdb: boolean;
  hasFulltext: boolean;
  refdbFullRecUrl: string;
  briefFields: CustomField[];
  candoCILL: boolean;
  isEContent: boolean;
  score: number;
  requestableRecordFormat: boolean;
  rawMarc: string;
  versoHoldings: VersoHolding;
  sourceKey: string;
  isMyLibrary: boolean;
}

export interface CustomField {
  tagId: number;
  label: string;
  value: string;
  sortOrder: number;
  hyperLinkText: string;
  hotLinkQuery: HotLinkQuery;
}

export interface HotLinkQuery {
  index: string;
  query: string;
}

export interface VersoHolding {
  databaseType: string;
  hHoldingId: string;
  holdingContent: string;
  agControlId: number;
  libraryDBPoolKey: number;
  totalLocations: number;
  totalCopies: number;
  totalAvailable: number;
  totalHolds: number;
  candoCILL: boolean;
  showAddItemLink: boolean;
  addItemLink: string;
  headers: string[];
  data: VersoHldData[];
}

export interface VersoHldData {
  lpk: number;
  libCode: string;
  barcode: string;
  locationId: number;
  item: FieldObject[];
}

export interface FieldObject {
  name: string;
  value: any;
}

export interface TargetSummary {
  name: string;
  hitCount: number;
  status: number;
  libraryDBPoolKey: number;
  type: string;
}

export interface Facet {
  rangeField: RangeField;
  id: number;
  category: string;
  count: number;
  show: boolean;
  fields: FacetField[];
  expandFacet?: boolean; // ui display
  showAllFacetFields?: boolean; // ui display
}

export interface RangeField {
  from: string;
  to: string;
}

export interface FacetField {
  facetMatch: FacetMatch;
  name: string;
  selected: boolean;
  value: string;
}

export interface FacetMatch {
  index: string;
  query: string;
}

export interface HoldingResult {
  holdings: Holding[];
  totalHoldings: number;
}

export interface Holding {
  agControlId: number;
  databaseType: string;
  holdingId: string;
  holdingContent: string;
}

export interface VersoHolding extends Holding {
  libraryDBPoolKey: number;
  totalLocations: number;
  totalCopies: number;
  totalAvailable: number;
  totalHolds: number;
  candoCILL: boolean;
  showAddItemLink: boolean;
  addItemLink: string;
  headers: string[];
  data: VersoHldData[];
}

export interface VersoHoldingRequest {
  searchId: string;
  versoHoldingRequestItems: VersoHoldingRequestItem[];
}

export interface VersoHoldingRequestItem {
  libraryDBPoolKey: number;
  agControlIds: string;
}

export interface SolrSpellSuggesterResponse {
  status: string;
  message: string;
  solrSpellSuggestions: SolrSpellSuggesterItem[];
}

export interface SolrSpellSuggesterItem {
  searchId: string;
  startRecord: number;
  numOfRecords: number;
  facetOffset: number;
  numOfFacetPerGroup: number;
  sortBy: string[];
  orderBy: string;
  resultType: string;
  includedVersoHoldings?: boolean;
  wait?: boolean;
  resultStatusOnly: boolean;
  suggestedTerm: string;
  query: string;
  hits: number;
}

export interface SearchTarget {
  breakoutFrame: boolean;
  catOrder: number;
  dataId: number;
  description: string;
  discription_DisplayMethod: number;
  isCollapse: boolean;
  isDefault: boolean;
  libraryDBpoolKey: number;
  noteId: number;
  noteName: string;
  noteText: string;
  noteUrl: string;
  sourcePoolKey: number;
  targetDescription: string;
  targetName: string;
  targetType: string;
  targetUrl: string;
}

export interface TargetGroups {
  heading: string;
  id: number;
  isDefault: boolean;
  checkedStatus: CHECKED_STATUS;
  numberOfTargets: number;
  searchTargets: SearchTarget[];
  isCollapse: boolean;
}

export interface AvailableTargetsInfo {
  targetGroups: TargetGroups[];
  totalGroups: number;
  totalTargets: number;
}

export interface SearchIndexes {
  simpleSearchIndexes: any[];
  advancedSearchIndexes: any[];
}

export interface FacetsPayload {
  facetMatches: FacetMatch[];
  filters: Filter;
  searchId: string;
  startRecord: number;
  numOfRecords: number;
  facetOffset: number;
  numOfFacetPerGroup: number;
  sortBy: string[];
  orderBy: string;
  resultType: string;
  resultStatusOnly: boolean;
}

export interface TypeAhead {
  heading: string;
  results: TypeAheadResult[];
}

export interface TypeAheadResult {
  term: string;
  termHtml: string;
  searchTerm: string;
  index: string;
  facet: FacetMatch;
}

export interface Option {
  name: string;
  value: string;
  description: string;
}

export interface ShelfStatusModel {
  xdbprofilekey: number;
  isbn: string;
}

export enum FacetIndex {
  LANGUAGE = 'lang',
  FORMAT_LONG = 'format_long',
  PUBLICATION_DATE = 'publication_date',
  HOLDING_MATERIAL_TYPE = 'hld_materialtype',
  AUDIENCE = 'audience',
  ACCELERATED_READER_INTEREST_LEVEL = 'accelerated_reader_interest_level',
  ACCELERATED_READER_READING_LEVEL = 'accelerated_reader_reading_level',
  ACCELERATED_READER_TITLE_POINT_VALUE = 'accelerated_reader_title_point_value',
  READING_COUNTS_INTEREST_LEVEL = 'reading_counts_interest_level',
  READING_COUNTS_READING_LEVEL = 'reading_counts_reading_level',
  READING_COUNTS_TITLE_POINT_VALUE = 'reading_counts_title_point_value',
  HAS_OCLC_NUMBER = 'has_oclc_number',
  LEXILE_READING_GRADE_LEVEL = 'lexile_reading_grade_level',
  AWARDS = 'awards',
  READING_PROGRAM = 'reading_program',
  DATE_ADDED = 'date_added',
  CLCD_AGE = 'clcd_age',
  CLCD_GRADES = 'clcd_grades',
}

export interface SearchAlert {
  id: number;
  userId: number;
  name: string;
  createDate: Date;
  lastSearchedDate: Date;
  searchQuery: SearchQuery;
}
